import {
  faEye,
  faMapMarkerAlt,
  faCalendarAlt,
  faSync,
  faChartLine,
  faDownload,
  faFileExport,
  faSyncAlt,
  faFilter,
  faHourglass,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DataTable from "components/DataTable";
import React, { useEffect, useState, useMemo, ReactNode, useCallback, useRef } from "react";
import httpClient from "services/network/httpClient";
import GenericModal from "components/Modal";
import { epochToDate } from "utility/dateFormat";


import { showToast } from "components/Toaster";
import DeleteConfirmationModal from "components/Modal/DeleteConfirmationModal";
import ViewVisitDetails from "./ViewVisitDetails";
import appConfig from "services/config";
import axios from "axios";
import { getFileNameFromDisposition } from "pages/summary/VisitReport";
import { getDefaultDateRange } from "pages/report/ReportList";
import DemoReportFilter, { FilterFormValues } from "pages/summary/demo/DemoReportFilter";
import { motion } from "framer-motion";
import CustomButton from "components/button/CustomButton";
import FilterBadge, { FilterChip } from "components/filter/FilterBadge";
import { msToDateInput } from "../../summary/DemoReport"
import DateTimeCell from "components/DataTable/DateTimeCell";
import FollowUpDateTimeCell from "components/DataTable/FolloUpDateTimeCell";


interface InfoDataprops {
  userId: string;
  employeeName: string;
  city: string;
  totalVisits: number;
}

interface VisitData {
  id: number;
  distributorName: string;
  imageUrl: string;
  latitude: number;
  longitude: number;
  note: string;
  createdDate: number;
  nextFollowUpVisit: number;
  completedDate?: number
  updatedDate?: number;
  isPending: boolean
}

const getSortIndicator = (field: string, activeField: string | null, direction: 'asc' | 'desc') => {
  if (activeField !== field) return '';
  return direction === 'asc' ? '↑' : '↓';
};

const compareValues = (a: unknown, b: unknown): number => {
  if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b);
  return Number(a) - Number(b);
};

interface UsersVisitListProps {
  infoData: InfoDataprops;
  onVisitCountUpdate?: (visitCount: number) => void;
  isHeaderRequired?: boolean; // New prop to control header display
  filterByToday?: boolean; // New prop to filter only today's visits
  filterParams?: string; // URL parameters from applied filters
  showCurrentMonthVisits?: boolean;
}


const UsersVisitList = ({ infoData, onVisitCountUpdate, isHeaderRequired = true, filterByToday = false, filterParams = "", showCurrentMonthVisits = true }: UsersVisitListProps) => {
  const [visitList, setVisitList] = useState<VisitData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<VisitData | null>(null);
  const [deleteItem, setDeleteItem] = useState<VisitData | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [activeFilterParams, setActiveFilterParams] = useState<Record<string, string>>({});
  const [activeFilterFormValues, setActiveFilterFormValues] = useState<Partial<FilterFormValues>>({});
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [url, setUrl] = useState("complete");
  const [showFilter, setShowFilter] = useState(false);
  const [filterBtnRef, setFilterBtnRef] = useState<HTMLButtonElement | null>(null);
  const [filterChips, setFilterChips] = useState<FilterChip[]>([]);
  const activeParamsRef = useRef<Record<string, string>>({});
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [isDownloading, setIsDownloading] = useState(false);
  const isDeletedMedia = (url?: string) => {
    if (!url) return true;

    return url.includes("amazonaws.com") && url.includes("_image");
  };
  console.log("Visit info: ", infoData);
  // Fetch data whenever the employee ID or filter parameters change
  useEffect(() => {
    if (infoData?.userId) {
      console.log(`Fetching visit data for employee ${infoData.userId} with filters: ${filterParams || 'default date range'}`);
      getVisit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoData?.userId, filterParams, url]);


  const getVisit = async (overrideParams?: Record<string, string>) => {
    const baseParams: Record<string, string> = overrideParams
      ? { ...overrideParams, page: "1", size: String(itemsPerPage) }
      : (() => {
        const p: Record<string, string> = Object.keys(activeFilterParams).length > 0
          ? { ...activeFilterParams }
          : (() => {
            const { fromDate, toDate } = getDefaultDateRange();
            return { fromDate, toDate };
          })();
        p.page = String(pageNumber);
        p.size = String(itemsPerPage);
        return p;
      })();
    // Always enforce status from active tab: complete → 0, follow-up → 1
    baseParams.status = url === "pending" ? "1" : "0";
    setIsLoading(true);

    await httpClient.get(`v1/visit/team/all-visits?empIds=${infoData.userId}&${new URLSearchParams(baseParams).toString()}`)
      .then((response: any) => {
        console.log("Getting Visit List: ", response);
        if (response.success) {
          setVisitList(response.value.contents ?? []);
          setTotalItems(response.value.totalElements ?? 0);
        }
      })
      .catch((error) => {
        console.log("Error Getting Visit:", error);
      })
      .finally(() => {
        setIsLoading(false);
      })
  }


  const handleDownload = () => {
    if (!selectedVisit?.imageUrl) return;

    // Extract filename from URL
    const filename = selectedVisit.imageUrl.split("/").pop()?.split("?")[0] || "download.jpg";

    try {
      // Try to download via <a> element
      const link = document.createElement("a");
      link.href = selectedVisit.imageUrl;
      link.download = filename;
      document.body.appendChild(link);

      // For cross-origin, some browsers ignore download attribute
      const clickEvent = new MouseEvent('click');
      link.dispatchEvent(clickEvent);

      document.body.removeChild(link);
    } catch (err) {
      console.error("Direct download failed, opening in new tab", err);

      // Fallback: open in new tab
      window.open(selectedVisit.imageUrl, "_blank", "noopener,noreferrer");
      alert("Download may be blocked by CORS. Image opened in a new tab, please save manually.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    try {
      await httpClient.delete(`/visit/${deleteItem.id}/image`);

      console.log("Image deleted successfully");

      // Update state to remove image URL from selected visit
      setVisitList((prev) =>
        prev.map((v) =>
          v.id === deleteItem.id ? { ...v, imageUrl: "" } : v
        )
      );

      if (selectedVisit?.id === deleteItem.id) {
        setSelectedVisit((prev) => prev ? { ...prev, imageUrl: "" } : null);
      }

      setIsDeleteOpen(false);
      setDeleteItem(null);
      showToast("Image deleted successfully", "success");
    } catch (error: any) {
      console.error("Delete failed", error);
      alert("Failed to delete image. Please try again.");
    }
  };

  const handleView = (row: VisitData) => {
    setSelectedVisit(row);
    setIsViewModalOpen(true);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  console.log("visitList", handleSort);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };


  const formattedData = useMemo(() => {
    return visitList.map(visit => {
      // Create a row object that matches Row interface with string indexer
      const row: any = {
        id: visit.id,
        distributorName: visit.distributorName,
        createdDate: visit.createdDate,
        nextFollowUpVisit: visit.nextFollowUpVisit,
        note: visit.note,
        location: `${visit.latitude},${visit.longitude}`,
        completedDate: visit.completedDate || 0,
        updatedDate: visit.updatedDate || 0,
        isPending: visit.isPending,
      };

      // Add a non-enumerable property to store the original data
      Object.defineProperty(row, '_originalData', {
        value: visit,
        enumerable: false
      });

      return row;
    });
  }, [visitList]);

  // Filter data based on search term only (date filtering is now handled by the API)
  const filteredData = useMemo(() => {
    let filtered = formattedData;

    // No need to filter by date here as it's now handled in the API call

    // Apply search term filter
    if (searchTerm.trim()) {
      const searchTermLower = searchTerm.toLowerCase();
      filtered = filtered.filter(row =>
        String(row.distributorName).toLowerCase().includes(searchTermLower) ||
        String(row.note).toLowerCase().includes(searchTermLower)
      );
    }

    return filtered;
  }, [formattedData, searchTerm]);

  const handleDownloadPdf = useCallback((row: VisitData) => {
    if (!row) {
      console.error("Attempted to download details for undefined row");
      return;
    }
    const token = localStorage.getItem("accessToken");
    const baseUrl = appConfig.getBaseUrl();

    axios.get(`${baseUrl}/reports/visit/${row.id}/pdf`, {
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" })
        );
        const disposition = response.headers["content-disposition"];
        console.log("This is the vist excell response: ", response);
        console.log("THis is the Visit excell export disposition : ", disposition);
        const fileName = getFileNameFromDisposition(disposition);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("Download PDF error: ", error);
      });
  }, []);

  // Sort data based on sort field and direction
  const sortedData = useMemo(() => {
    if (!sortField) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      if (aValue === undefined || bValue === undefined) return 0;
      const comparison = compareValues(aValue, bValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortField, sortDirection]);

  const hasFollowUp = sortedData?.some((row: any) => row?.isPending === false);
  console.log("Has follow up", hasFollowUp);

  const columns = useMemo(() => [
    {
      key: "distributorName",
      label: `Distributor Name ${getSortIndicator('distributorName', sortField, sortDirection)}`,
      render: (value: ReactNode) => (
        <div className="font-medium text-blue-700">{value}</div>
      )
    },
    {
      key: "createdDate",
      label: `Visit Date ${getSortIndicator('createdDate', sortField, sortDirection)}`,
      sortable: true,
      render: (value: ReactNode) =>
        <DateTimeCell value={value as number} />
    },
    // Only show these columns if isPending is false for any row
    ...(hasFollowUp
      ? [
        {
          key: "completedDate",
          label: "Completed",
          render: (value: ReactNode, row: any) =>
            !row.isPending ? <DateTimeCell value={value as number} row={row} /> : <span>N/A</span>
        },
        {
          key: "nextFollowUpVisit",
          label: "Follow Up",
          render: (value: ReactNode, row: any) =>
            !row.isPending ? <FollowUpDateTimeCell value={value as number} /> : <span>N/A</span>
        },
        {
          key: "note",
          label: `Note ${getSortIndicator('note', sortField, sortDirection)}`,
          render: (value: ReactNode, row: any) => (
            !row.isPending && <div className="max-w-xs truncate" title={value as string}>
              {value}
            </div>
          )
        },
      ]
      : []),

    {
      key: "actions",
      label: "Actions",
      excludeExport: true,
      actions: (row: any) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleView(row._originalData)}
            className="px-4 py-2 
            bg-blue-50 dark:bg-blue-900/30 
            hover:bg-blue-100 dark:hover:bg-blue-900/50 
            text-blue-700 dark:text-blue-300 
            rounded-lg 
            transition-colors duration-200 
            flex items-center"
          >
            <FontAwesomeIcon icon={faEye} className="" />
          </button>
          <button
            onClick={() => handleDownloadPdf(row._originalData)}
            className="px-4 py-2 
            bg-blue-50 dark:bg-blue-900/30 
            hover:bg-blue-100 dark:hover:bg-blue-900/50 
            text-blue-700 dark:text-blue-300 
            rounded-lg 
            transition-colors duration-200 
            flex items-center"
          >
            <FontAwesomeIcon icon={faDownload} className="" />
          </button>
        </div>
      ),
    },
  ], [sortField, sortDirection, sortedData]);

  const handleDeleteIcon = () => {
    setDeleteItem(selectedVisit);
    setIsDeleteOpen(true);
  }
  const normalizeData = (resp: any, url: string) => {
    // API sometimes returns results as resp.value.contents (paged)
    // or as resp.value.content or as resp.value (direct array). Prefer detecting the shape.
    if (Array.isArray(resp?.value?.contents)) {
      return resp.value.contents;
    }
    if (Array.isArray(resp?.value?.content)) {
      return resp.value.content;
    }
    if (Array.isArray(resp?.value)) {
      return resp.value;
    }
    if (Array.isArray(resp?.contents)) {
      return resp.contents;
    }
    if (Array.isArray(resp?.content)) {
      return resp.content;
    }
    return [];
  };
  const removeChip = (chipId: string, paramKeys: string[], formKeys: string[]) => {
    // Read from ref — always current even in stale closures
    const newParams = { ...activeParamsRef.current };
    if (chipId === "dateRange") {
      const { fromDate, toDate } = getDefaultDateRange();
      newParams.fromDate = `${fromDate}`;
      newParams.toDate = `${toDate}`;
    } else {
      paramKeys.forEach(k => delete newParams[k]);
    }
    setFilterChips(prev => prev.filter(c => c.id !== chipId));
    activeParamsRef.current = newParams;
    setActiveFilters(newParams);
    setActiveFilterFormValues(prev => {
      const next = { ...prev } as any;
      // Always store as string for popup
      next.fromDate = msToDateInput(newParams.fromDate);
      next.toDate = msToDateInput(newParams.toDate);
      formKeys.forEach(k => { if (k !== "fromDate" && k !== "toDate") delete next[k]; });
      return next;
    });
    setIsLoading(true);
    httpClient
      .get(`v1/visit/team/all-visits?empIds=${infoData?.userId}&${new URLSearchParams(newParams).toString()}`)
      .then((resp: any) => {
        setVisitList(resp?.value?.contents ?? []);
        setTotalItems(resp?.value?.totalElements ?? 0);
      })
      .catch((error) => {
        console.error("Error fetching data after chip removal:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFilterSubmit = (filterData: any) => {
    console.log("Filter data submitted:", filterData);

    const params: Record<string, string> = {};
    if (filterData.fromDate) params.fromDate = `${filterData.fromDate}`;
    if (filterData.toDate) params.toDate = `${filterData.toDate}`;
    if (filterData.manager && filterData.manager !== "All") params.managerId = filterData.manager;
    if (filterData.employee && filterData.employee !== "All") params.empIds = filterData.employee;
    if ((filterData as any).product && (filterData as any).product !== "All") params.productId = (filterData as any).product;
    if ((filterData as any).crop && (filterData as any).crop !== "All") params.cropId = (filterData as any).crop;
    if ((filterData as any).distributor && (filterData as any).distributor !== "All") params.distributor = (filterData as any).distributor;

    // Always enforce status based on active tab: complete → 0, pending (follow-up) → 1
    params.status = url === "pending" ? "1" : "0";

    // Store active filters
    activeParamsRef.current = params;
    setActiveFilters(params);
    // Always store as string for popup
    setActiveFilterFormValues({
      ...filterData,
      fromDate: msToDateInput(params.fromDate),
      toDate: msToDateInput(params.toDate),
    });

    // Build filter chips
    const chips: FilterChip[] = [];
    if (params.fromDate || params.toDate) {
      chips.push({
        id: "dateRange",
        label: `Date: ${msToDateInput(params.fromDate)} – ${msToDateInput(params.toDate)}`,
        onRemove: () => removeChip("dateRange", ["fromDate", "toDate"], ["fromDate", "toDate"]),
      });
    }
    if (params.managerId) {
      chips.push({
        id: "manager",
        label: `Manager: ${filterData.__managerLabel ?? params.managerId}`,
        onRemove: () => removeChip("manager", ["managerId"], ["manager"]),
      });
    }
    if (params.empIds) {
      chips.push({
        id: "employee",
        label: `Employee: ${filterData.__employeeLabel ?? params.empIds}`,
        onRemove: () => removeChip("employee", ["empIds"], ["employee"]),
      });
    }
    if (params.productId) {
      chips.push({
        id: "product",
        label: `Product: ${filterData.__productLabel ?? params.productId}`,
        onRemove: () => removeChip("product", ["productId"], ["product"]),
      });
    }
    if (params.cropId) {
      chips.push({
        id: "crop",
        label: `Crop: ${filterData.__cropLabel ?? params.cropId}`,
        onRemove: () => removeChip("crop", ["cropId"], ["crop"]),
      });
    }
    setFilterChips(chips);

    // Reset to first page when applying filters
    setPageNumber(1);

    console.log("Filter params:", params.toString());

    // Call API with filters
    setIsLoading(true);
    httpClient
      .get(`v1/visit/team/all-visits?empIds=${infoData?.userId}&${new URLSearchParams(params).toString()}`)
      .then((resp: any) => {
        setVisitList(resp?.value?.contents ?? []);
        setTotalItems(resp?.value?.totalElements ?? 0);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching filtered data:", error);
        setIsLoading(false);
      });

    // Close filter form
    setShowFilter(false);
  };

  const resetFilters = () => {
    activeParamsRef.current = {};
    setActiveFilters({});
    setActiveFilterParams({});
    setActiveFilterFormValues({});
    setFilterChips([]);
    setPageNumber(1);
    getVisit({});
  };

  const handleRefresh = () => getVisit();

  const handleDownloadExcel = async () => {
    const token = localStorage.getItem("accessToken");
    const baseUrl = appConfig.getBaseUrl();
    console.log("Base Url from Visit report: ", baseUrl);

    // Use active filter params if the user applied a filter, otherwise fall back to default date range
    const params: Record<string, string> = Object.keys(activeFilterParams).length > 0
      ? { ...activeFilterParams }
      : (() => {
        const { fromDate, toDate } = getDefaultDateRange();
        return { fromDate, toDate };
      })();
    setIsDownloading(true);
    params.status = url === "pending" ? "1" : "0";

    await axios.get(
      `${baseUrl}/v1/visit/team/export?empIds=${infoData?.userId}&${new URLSearchParams(params).toString()}`,
      {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      }
    )
      .then((response) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        const disposition = response.headers["content-disposition"];
        // const fileName = disposition
        //     ? (disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]*)\1/)?.[2]?.trim() ?? `report-${Date.now()}.xlsx`)
        //     : `report-${Date.now()}.xlsx`;
        const fileName = getFileNameFromDisposition(disposition);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("Generate Report error: ", error);
      })
      .finally(() => {
        setIsDownloading(false);
      })
  };

  return (
    <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-2">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-gray-200 dark:border-gray-700 ">
        <div className="flex mb-4 md:mb-0">
          <motion.button
            whileHover={{ y: -2 }}
            whileTap={{ y: 0 }}
            onClick={() => setUrl("complete")}
            className={`px-4 py-2 font-medium text-sm md:text-base rounded-t-lg transition-all duration-200 flex items-center ${url === "complete"
              ? "text-green-700 dark:text-green-400 border-b-2 border-green-700 dark:border-green-400 bg-green-50 dark:bg-green-900/20"
              : "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
              }`}
          >
            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
            Completed
          </motion.button>
          <motion.button
            whileHover={{ y: -2 }}
            whileTap={{ y: 0 }}
            onClick={() => setUrl("pending")}
            className={`px-4 py-2 font-medium text-sm md:text-base rounded-t-lg transition-all duration-200 flex items-center ${url === "pending"
              ? "text-amber-700 dark:text-amber-400 border-b-2 border-amber-500 dark:border-amber-400 bg-amber-50 dark:bg-amber-900/20"
              : "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
              }`}
          >
            <FontAwesomeIcon icon={faHourglass} className="mr-2" />
            Follow Up
          </motion.button>
        </div>

        <div className="flex items-center space-x-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search farmer, crop, location..."
              className="pl-10 pr-4 py-2 text-sm border dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              onChange={(e) => setSearchTerm(e.target.value)}
            />

          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            ref={setFilterBtnRef as any}
            onClick={() => setShowFilter(!showFilter)}
            className="relative px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-800/40 transition-colors duration-200 flex items-center"
          >
            <FontAwesomeIcon icon={faFilter} className="mr-1.5" />
            <span>Filter</span>
            {filterChips.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-[10px] text-white font-bold">
                {filterChips.length}
              </span>
            )}
          </motion.button>


          {/* <CustomButton
            onClick={handleDownloadExcel}
          >
            <FontAwesomeIcon icon={faFileExport} />
            <span className="ml-1 hidden md:inline">Download</span>
          </CustomButton> */}
          <CustomButton onClick={handleDownloadExcel} loading={isDownloading}> {!isDownloading && <FontAwesomeIcon icon={faDownload} className="mr-1.5" />} Download</CustomButton>


          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleRefresh}
            className="p-2 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-800/40 transition-colors duration-200 flex items-center"
          >
            <FontAwesomeIcon icon={faSyncAlt} className={isLoading ? "animate-spin" : ""} />
            <span className="ml-1 hidden md:inline">Refresh</span>
          </motion.button>
        </div>
      </div>

      {/* Filter Badge Strip */}
      {filterChips.length > 0 && (
        <FilterBadge
          count={filterChips.length}
          onClear={resetFilters}
          chips={filterChips}
        />
      )}

      {/* Stats Section */}
      {
        isHeaderRequired && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg shadow-sm">
              <h3 className="text-sm font-medium text-blue-700 mb-1">{filterByToday ? "Today's Visits" : "Total Visits"}</h3>
              <p className="text-2xl font-bold text-blue-800">{visitList.length}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg shadow-sm">
              <h3 className="text-sm font-medium text-green-700 mb-1">Upcoming Follow-ups</h3>
              <p className="text-2xl font-bold text-green-800">
                {visitList.filter(v => v.nextFollowUpVisit && v.nextFollowUpVisit > Date.now() / 1000).length}
              </p>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg shadow-sm">
              <h3 className="text-sm font-medium text-amber-700 mb-1">{filterByToday ? "Latest Visits" : "Recent Visits"}</h3>
              <p className="text-2xl font-bold text-amber-800">
                {visitList.filter(v => v.createdDate > (Date.now() / 1000 - 7 * 24 * 60 * 60)).length}
              </p>
            </div>
          </div>
        )
      }

      {/* Loading State */}
      {
        isLoading ? (
          <div className="flex justify-center items-center ">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          /* Data Table */
          <DataTable
            columns={columns}
            data={sortedData as any}
            isAddBtnTrue={false}
            isSearchBtn={false}
            isExport={false}
            defaultSortColumn="createdDate"
            defaultSortDirection="desc"
          />
        )
      }

      {/* View Details Modal */}
      {
        isViewModalOpen && selectedVisit && (
          <GenericModal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            header={`Visit to ${selectedVisit.distributorName}`}
            size="lg"
            // customWidth="md:max-w-lg"
            customWidth="md:max-w-6xl"
          >

            <ViewVisitDetails
              selectedVisit={selectedVisit}
              handleDownload={handleDownload}
              handleDeleteIcon={handleDeleteIcon}
              isDeletedMedia={isDeletedMedia}
            />
          </GenericModal>
        )
      }
      {/* ✅ Delete Confirmation Modal */}
      {
        isDeleteOpen && deleteItem && (
          <DeleteConfirmationModal
            isOpen={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            onConfirm={confirmDelete} // calls your delete function
            message={`Are you sure you want to delete this image for ${deleteItem.distributorName}?`}
            showDownloadWarning={true}
          />
        )
      }
      {showFilter && filterBtnRef && (
        <div
          style={{
            position: 'absolute',
            zIndex: 50,
            right: '8rem',
            top: `${filterBtnRef.getBoundingClientRect().bottom + window.scrollY + 10}px`,
            width: '400px',
            maxWidth: 'calc(100vw - 2rem)'
          }}
        >

          <DemoReportFilter
            onSubmit={handleFilterSubmit}
            onClose={() => setShowFilter(false)}
            showManager={false}
            showEmployee={false}
            initialValues={activeFilterFormValues}
            title="Filter Visit Details"
          ></DemoReportFilter>
        </div>
      )}



    </div >
  );
}

export default UsersVisitList;

function getCurrentMonthDates(): { fromDateStr: string; toDateStr: string } {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const fromDateStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const toDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { fromDateStr, toDateStr };
}
